import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'use-http';
import { getLeaseRecordsApiErrorMessage } from '../../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';
import { FileText, Loader2, X, AlertTriangle } from 'src/components/common/icons';
import { DateField, Field, formatCurrency, SelectField } from '../Master';
import useCheckNumberValidation from './useCheckNumberValidation';

interface ICreateRecordingFeesCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    leaseMaster: ILeaseMaster;
}

interface IInvoiceRow {
    lsNo: number;
    liTg: number;
    lrTg: number;
    invoice: string;
    state: string;
    county: string;
    recFee: number;
    instrument: string;
    lessor: string;
}

interface ICompany {
    id: number;
    name: string;
}

interface ICountyClerk {
    county: string;
    addr?: string;
    city?: string;
    state: string;
    zip?: string;
}

interface ISelectOption {
    value: string;
    label: string;
}

export const getCountyPayee = (county: string, state: string): string => `${county}${state === 'KS' ? ' County Register of Deeds' : ' County Clerk'}`;

export const getCountyCstz = (clerk: ICountyClerk): string =>
    [clerk.city, [clerk.state, clerk.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

export const getRecordingFeeRowId = (invoice: { lrTg: number }): number => invoice.lrTg;

export const serializeRecordingFeeRowIds = (lrTgs: Set<number>): string => JSON.stringify(Array.from(lrTgs));

const CreateRecordingFeesCheckModal: React.FC<ICreateRecordingFeesCheckModalProps> = ({ isOpen, onClose, onSuccess, leaseMaster }) => {
    const { get: getCompanies, response: companiesResponse } = useFetch();
    const { get: getDefaults, response: defaultsResponse } = useFetch();
    const { get: getInvoices, response: invoicesResponse } = useFetch();
    const { get: getLocationOptions } = useFetch();
    const { get: getClerk } = useFetch();
    const { get: getCheckDetails, post: createCheck, response: createResponse } = useFetch();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [isLoadingDefaults, setIsLoadingDefaults] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    const [clNo, setClNo] = useState<string>(leaseMaster.clNo?.toString() || '');
    const [prNo, setPrNo] = useState<string>(leaseMaster.prNo?.toString() || '');
    const [contextError, setContextError] = useState<string | null>(null);

    const [stateVal, setStateVal] = useState<string>('');
    const [countyVal, setCountyVal] = useState<string>('');
    const [stateOptions, setStateOptions] = useState<ISelectOption[]>([]);
    const [countyOptions, setCountyOptions] = useState<ISelectOption[]>([]);
    const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);

    const [checkDate, setCheckDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [checkNo, setCheckNo] = useState<string>('');
    const [compId, setCompId] = useState<string>('');
    const [memo, setMemo] = useState<string>('');

    const [payee, setPayee] = useState('');
    const [addr1, setAddr1] = useState('');
    const [addr2, setAddr2] = useState('');
    const [cstz, setCstz] = useState('');

    const [invoices, setInvoices] = useState<IInvoiceRow[]>([]);
    const [selectedLrTgs, setSelectedLrTgs] = useState<Set<number>>(new Set());
    const [checkTotal, setCheckTotal] = useState<number>(0.0);

    const { isValidating, validationError, isCheckAvailable } = useCheckNumberValidation({
        checkNo,
        compId,
        missingCompanyMessage: 'Select Company First',
        requestFailureMessage: 'Validation Error',
    });

    const [createdCheck, setCreatedCheck] = useState<ILeaseCheck | null>(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            setClNo(leaseMaster.clNo?.toString() || '');
            setPrNo(leaseMaster.prNo?.toString() || '');

            const init = async () => {
                await Promise.all([loadCompanies(), loadStates()]);
            };
            init();
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const resetInvoiceResults = (): void => {
        setInvoices([]);
        setSelectedLrTgs(new Set());
        setCheckTotal(0);
    };

    const clearContextDefaults = (): void => {
        setStateVal('');
        setCountyVal('');
        setPayee('');
        setAddr1('');
        setAddr2('');
        setCstz('');
        setCountyOptions([]);
    };

    const resetForm = () => {
        resetInvoiceResults();
        setCheckNo('');
        setIsLoadingDefaults(false);
        setCreatedCheck(null);
        setErrorMsg(null);
        setContextError(null);
        setCompId('');
        clearContextDefaults();
    };

    const loadCompanies = async () => {
        setLoading(true);
        try {
            const comps = await getCompanies('/v1.0/leases/master/companies');
            if (isMounted.current && companiesResponse.ok) {
                if (Array.isArray(comps)) {
                    setCompanies(comps);
                    if (comps.length > 0) {
                        setCompId(comps[0].id.toString());
                    }
                }
            }
        } catch (e) {
            // ignore
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const loadStates = async (): Promise<void> => {
        try {
            const states = await getLocationOptions('/v1.0/leases/states');
            if (isMounted.current && Array.isArray(states)) {
                setStateOptions(states.map((state: { stCode: string; abbr: string }) => ({ value: state.stCode, label: state.abbr })));
            }
        } catch (e) {
            if (isMounted.current) {
                setStateOptions([]);
            }
        }
    };

    const loadCounties = async (state: string): Promise<void> => {
        if (!state) {
            setCountyOptions([]);
            return;
        }

        setIsLoadingLocation(true);
        try {
            const counties = await getLocationOptions(`/v1.0/leases/states/${encodeURIComponent(state)}/counties`);
            if (isMounted.current) {
                setCountyOptions(
                    Array.isArray(counties) ? counties.map((county: { county: string }) => ({ value: county.county, label: county.county })) : []
                );
            }
        } catch (e) {
            if (isMounted.current) {
                setCountyOptions([]);
            }
        } finally {
            if (isMounted.current) {
                setIsLoadingLocation(false);
            }
        }
    };

    const clearPayeeDetails = (): void => {
        setPayee('');
        setAddr1('');
        setAddr2('');
        setCstz('');
    };

    const handleStateChange = (state: string): void => {
        setStateVal(state);
        setCountyVal('');
        clearPayeeDetails();
        resetInvoiceResults();
        void loadCounties(state);
    };

    const handleCountyChange = async (county: string): Promise<void> => {
        setCountyVal(county);
        clearPayeeDetails();
        resetInvoiceResults();

        if (!stateVal || !county) {
            return;
        }

        setIsLoadingLocation(true);
        try {
            const result = await getClerk(
                `/v1.0/leases/admin/clerks?skip=0&top=25&state=${encodeURIComponent(stateVal)}&county=${encodeURIComponent(county)}`
            );
            if (!isMounted.current) {
                return;
            }

            const clerks: ICountyClerk[] = Array.isArray(result?.items) ? result.items : [];
            const clerk = clerks.find((item) => item.state === stateVal && item.county === county);
            setPayee(getCountyPayee(county, stateVal));
            if (clerk) {
                setAddr1(clerk.addr || '');
                setAddr2('');
                setCstz(getCountyCstz(clerk));
            }
        } catch (e) {
            if (isMounted.current) {
                setPayee(getCountyPayee(county, stateVal));
            }
        } finally {
            if (isMounted.current) {
                setIsLoadingLocation(false);
            }
        }
    };

    const isValidContextNumber = (value: string): boolean => /^[1-9]\d*$/.test(value.trim());

    useEffect(() => {
        if (!isOpen || !isValidContextNumber(clNo) || !isValidContextNumber(prNo)) {
            setIsLoadingDefaults(false);
            return () => {
                /* noop */
            };
        }

        let isActive = true;
        const timerId = window.setTimeout(async () => {
            setIsLoadingDefaults(true);
            try {
                const defaults = await getDefaults(
                    `/v1.0/leases/master/recording-fee-defaults?clNo=${encodeURIComponent(clNo.trim())}&prNo=${encodeURIComponent(prNo.trim())}`
                );
                if (isActive && isMounted.current && defaultsResponse.ok) {
                    setStateVal(defaults?.state || '');
                    setCountyVal(defaults?.county || '');
                    setPayee(defaults?.payee || '');
                    setAddr1(defaults?.addr1 || '');
                    setAddr2(defaults?.addr2 || '');
                    setCstz(defaults?.cstz || '');
                    await loadCounties(defaults?.state || '');
                } else if (isActive && isMounted.current) {
                    setErrorMsg('Unable to load defaults for the entered Client and Project numbers.');
                }
            } catch (e) {
                if (isActive && isMounted.current) {
                    setErrorMsg('Unable to load defaults for the entered Client and Project numbers.');
                }
            } finally {
                if (isActive && isMounted.current) {
                    setIsLoadingDefaults(false);
                }
            }
        }, 400);

        return () => {
            isActive = false;
            window.clearTimeout(timerId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clNo, isOpen, prNo]);

    const handleContextChange = (field: 'clNo' | 'prNo', value: string): void => {
        if (field === 'clNo') {
            setClNo(value);
        } else {
            setPrNo(value);
        }
        setContextError(null);
        setErrorMsg(null);
        setIsLoadingDefaults(false);
        clearContextDefaults();
        resetInvoiceResults();
    };

    const handleQuery = async () => {
        if (!clNo.trim() || !prNo.trim()) {
            setContextError('Client No and Project No are required.');
            return;
        }
        if (!isValidContextNumber(clNo) || !isValidContextNumber(prNo)) {
            setContextError('Client No and Project No must be positive whole numbers.');
            return;
        }
        if (!stateVal || !countyVal) {
            setContextError('State and County are required to query.');
            return;
        }
        setLoading(true);
        setErrorMsg(null);
        setContextError(null);
        try {
            const url =
                `/v1.0/leases/master/recording-fee-invoices?clNo=${encodeURIComponent(clNo.trim())}` +
                `&prNo=${encodeURIComponent(prNo.trim())}&state=${encodeURIComponent(stateVal)}&county=${encodeURIComponent(countyVal)}`;
            const data = await getInvoices(url);
            if (isMounted.current) {
                if (invoicesResponse.ok) {
                    setInvoices(data || []);
                    setSelectedLrTgs(new Set());
                } else {
                    setErrorMsg('Failed to load invoices.');
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error while querying.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        let total = 0;
        invoices.forEach((inv) => {
            if (selectedLrTgs.has(inv.lrTg)) {
                total += inv.recFee || 0;
            }
        });
        setCheckTotal(total);
    }, [selectedLrTgs, invoices]);

    const toggleSelectAll = () => {
        if (selectedLrTgs.size === invoices.length && invoices.length > 0) {
            setSelectedLrTgs(new Set());
        } else {
            const allIds = new Set(invoices.map(getRecordingFeeRowId));
            setSelectedLrTgs(allIds);
        }
    };

    const toggleRow = (id: number) => {
        const next = new Set(selectedLrTgs);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedLrTgs(next);
    };

    const handleCreate = async () => {
        if (!checkNo || parseInt(checkNo, 10) <= 0) {
            return;
        }
        if (checkTotal <= 0) {
            return;
        }
        if (selectedLrTgs.size === 0) {
            return;
        }
        if (!isCheckAvailable) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                CheckNo: parseInt(checkNo, 10),
                CheckDate: checkDate,
                CheckAmount: checkTotal,
                CompID: parseInt(compId, 10),
                LrTgList: serializeRecordingFeeRowIds(selectedLrTgs),
                AccountType: 'L',
                Payee: payee,
                Addr1: addr1,
                Addr2: addr2,
                CSTZ: cstz,
                MemoFor: memo,
            };

            await createCheck('/v1.0/leases/master/process-recording-fee-check', payload);

            if (!isMounted.current) {
                return;
            }

            if (createResponse.ok) {
                onSuccess?.();
                const checkDetails = await getCheckDetails(`/v1.0/leases/master/checks/${payload.CheckNo}?compId=${payload.CompID}`);
                if (createResponse.ok && checkDetails && checkDetails.length > 0) {
                    setCreatedCheck(checkDetails[0]);
                } else {
                    onClose();
                }
            } else {
                setErrorMsg(getLeaseRecordsApiErrorMessage(createResponse.data, createResponse.status));
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg(getLeaseRecordsApiErrorMessage(undefined, 500));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const renderCheckNoLabel = () => {
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Check No.</span>
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600 }}>
                    {isValidating && (
                        <>
                            <Loader2 style={styles.spinner} />
                            <span style={{ color: '#6B7280' }}>Checking...</span>
                        </>
                    )}
                    {!isValidating && validationError && <span style={styles.errorText}>{validationError}</span>}
                    {!isValidating && isCheckAvailable && <span style={styles.successText}>Available</span>}
                </span>
            </span>
        );
    };

    const isCreateDisabled = loading || !compId || !isCheckAvailable || selectedLrTgs.size === 0 || checkTotal <= 0;

    if (!isOpen) {
        return null;
    }

    if (createdCheck) {
        return <CheckDetailsModal check={createdCheck} onClose={onClose} />;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        Recording Fee Check
                    </h2>
                    <button onClick={onClose} style={styles.closeButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                <div style={styles.modalBody}>
                    {errorMsg && (
                        <div style={styles.errorState} role="alert">
                            <AlertTriangle style={styles.errorIcon} />
                            {errorMsg}
                        </div>
                    )}
                    {contextError && (
                        <div style={styles.errorState}>
                            <AlertTriangle style={styles.errorIcon} />
                            {contextError}
                        </div>
                    )}

                    <div style={styles.formContainer}>
                        <div style={styles.col}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ flex: 1 }}>
                                    <Field
                                        label="Client No"
                                        value={clNo}
                                        type="number"
                                        editable={!loading}
                                        onChange={(value) => handleContextChange('clNo', value)}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Field
                                        label="Project No"
                                        value={prNo}
                                        type="number"
                                        editable={!loading}
                                        onChange={(value) => handleContextChange('prNo', value)}
                                    />
                                </div>
                            </div>

                            <div style={styles.queryRow}>
                                <div style={{ flex: 1 }}>
                                    <SelectField
                                        label="State"
                                        value={stateVal}
                                        options={stateOptions}
                                        onChange={handleStateChange}
                                        editable={!loading && !isLoadingDefaults && !isLoadingLocation}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <SelectField
                                        label="County"
                                        value={countyVal}
                                        options={countyOptions}
                                        onChange={(value) => void handleCountyChange(value)}
                                        editable={!loading && !isLoadingDefaults && !isLoadingLocation && !!stateVal}
                                    />
                                </div>
                                <button onClick={handleQuery} style={styles.queryBtn} disabled={loading || isLoadingDefaults}>
                                    {(loading || isLoadingDefaults) && <Loader2 style={styles.spinner} />} Query
                                </button>
                            </div>

                            <Field label={renderCheckNoLabel() as unknown as string} value={checkNo} onChange={setCheckNo} editable={!loading} />

                            <DateField label="Check Date" value={checkDate} onChange={setCheckDate} editable={!loading} />

                            <Field label="Check Total" value={checkTotal.toFixed(2)} prefix="$" editable={false} />

                            <Field label="Memo" value={memo} onChange={setMemo} editable={!loading} />
                        </div>

                        <div style={styles.col}>
                            <Field label="Payee" value={payee} onChange={setPayee} editable={!loading && !isLoadingDefaults} />
                            <Field label="Address" value={addr1} onChange={setAddr1} editable={!loading && !isLoadingDefaults} />
                            <Field label="" value={addr2} onChange={setAddr2} editable={!loading && !isLoadingDefaults} />
                            <Field label="City, ST, Zip" value={cstz} onChange={setCstz} editable={!loading && !isLoadingDefaults} />

                            <div style={{ marginTop: '10px' }}>
                                <SelectField
                                    label="Company (Required)"
                                    value={compId}
                                    onChange={setCompId}
                                    editable={!loading}
                                    options={[
                                        { value: '', label: 'Select...' },
                                        ...companies.map((c) => ({ value: c.id.toString(), label: c.name })),
                                    ]}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={styles.tableWrapper}>
                        <table style={styles.tableBase}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={{ ...styles.tableHeaderCell, width: '40px', textAlign: 'center' }}>
                                        <LeaseRecordInput
                                            type="checkbox"
                                            checked={invoices.length > 0 && selectedLrTgs.size === invoices.length}
                                            onChange={toggleSelectAll}
                                            disabled={loading}
                                        />
                                    </th>
                                    <th style={styles.tableHeaderCell}>Lease No</th>
                                    <th style={styles.tableHeaderCell}>Invoice No</th>
                                    <th style={styles.tableHeaderCell}>State</th>
                                    <th style={styles.tableHeaderCell}>County</th>
                                    <th style={styles.tableHeaderCell}>Rec. Fee</th>
                                    <th style={styles.tableHeaderCell}>Instrument</th>
                                    <th style={styles.tableHeaderCell}>Lessor</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {invoices.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>
                                            No invoices loaded. Verify State/County and click Query.
                                        </td>
                                    </tr>
                                ) : (
                                    invoices.map((inv) => (
                                        <tr key={inv.lrTg} style={styles.tableRow}>
                                            <td style={{ ...styles.tableDataCell, textAlign: 'center' }}>
                                                <LeaseRecordInput
                                                    type="checkbox"
                                                    checked={selectedLrTgs.has(inv.lrTg)}
                                                    onChange={() => toggleRow(inv.lrTg)}
                                                    disabled={loading}
                                                />
                                            </td>
                                            <td style={styles.tableDataCell}>{inv.lsNo}</td>
                                            <td style={styles.tableDataCell}>{inv.invoice}</td>
                                            <td style={styles.tableDataCell}>{inv.state}</td>
                                            <td style={styles.tableDataCell}>{inv.county}</td>
                                            <td style={styles.tableDataCell}>{formatCurrency(inv.recFee)}</td>
                                            <td style={styles.tableDataCell}>{inv.instrument}</td>
                                            <td style={styles.tableDataCell}>{inv.lessor}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div style={styles.modalFooter}>
                    <button onClick={toggleSelectAll} style={{ ...styles.buttonSecondary, marginRight: 'auto' }}>
                        Select All
                    </button>
                    <button onClick={() => setSelectedLrTgs(new Set())} style={{ ...styles.buttonSecondary }}>
                        Deselect All
                    </button>

                    <div style={{ flex: 1 }} />
                    <button onClick={onClose} style={{ ...styles.buttonSecondary, marginRight: 'auto' }} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        style={{
                            ...styles.buttonPrimary,
                            opacity: isCreateDisabled ? 0.5 : 1,
                            cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                            marginRight: 'auto',
                        }}
                        disabled={isCreateDisabled}
                    >
                        {loading ? 'Processing...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '95%',
        maxWidth: '1100px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' },
    formContainer: { display: 'flex', gap: '2rem', marginBottom: '1.5rem' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
    queryRow: { display: 'flex', alignItems: 'flex-end', gap: '10px' },
    queryBtn: {
        padding: '0 1rem',
        height: '38px',
        borderRadius: '0.375rem',
        backgroundColor: '#F3F4F6',
        border: '1px solid #D1D5DB',
        cursor: 'pointer',
        fontWeight: 500,
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
    },
    select: {
        padding: '0.4rem 0.8rem',
        borderRadius: '0.375rem',
        border: '1px solid #D1D5DB',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
    },
    tableWrapper: { overflowY: 'auto', flexGrow: 1, borderRadius: '0.5rem', border: '1px solid #E5E7EB', minHeight: '200px' },
    tableBase: { minWidth: '100%', borderCollapse: 'collapse' },
    tableHead: { backgroundColor: '#F9FAFB', position: 'sticky', top: 0, zIndex: 10 },
    tableHeaderCell: {
        padding: '0.75rem 0.5rem',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #E5E7EB',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { transition: 'background-color 0.1s ease' },
    tableDataCell: { padding: '0.5rem', fontSize: '0.875rem', color: '#111827', borderBottom: '1px solid #F3F4F6' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#6B7280', marginRight: '4px' },
    errorText: { color: '#EF4444' },
    successText: { color: '#10B981' },
};

export default CreateRecordingFeesCheckModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/CreateRecordingFeeCheckModal.tsx